<template>
  <div class="workflow-actions">
    <!-- Diagnoses Section -->
    <div class="mb-3">
      <h6 class="mb-2">Diagnoses *</h6>

      <!-- Diagnosis Dropdown -->
      <select
        v-model="selectedDiagnosisToAdd"
        class="form-select mb-2"
        @change="addDiagnosis"
      >
        <option value="">+ Select diagnosis...</option>
        <option
          v-for="diagnosis in availableDiagnosisOptions"
          :key="`diagnosis-option-${diagnosis.value}`"
          :value="diagnosis.value"
        >
          {{ diagnosis.text }}
        </option>
      </select>

      <!-- Custom Diagnosis Input (shows when "Other" is selected) -->
      <input
        v-if="isOtherDiagnosisSelected"
        v-model="customDiagnosisText"
        type="text"
        class="form-control form-control-sm mb-2"
        placeholder="Enter custom diagnosis..."
        @keyup.enter="addCustomDiagnosis"
      />

      <!-- Selected Diagnoses Pills -->
      <div v-if="selectedDiagnoses.length > 0" class="selected-pills">
        <span
          v-for="diagnosis in selectedDiagnoses"
          :key="`selected-diagnosis-${diagnosis}`"
          class="pill diagnosis-pill"
        >
          {{ getDiagnosisLabel(diagnosis) }}
          <font-awesome-icon
            icon="times"
            class="pill-remove"
            @click="removeDiagnosis(diagnosis)"
            title="Remove"
          />
        </span>
      </div>
    </div>

    <!-- Tags Section -->
    <div class="mb-3">
      <h6 class="mb-2">Tags</h6>

      <!-- Tag Dropdown -->
      <select
        v-model="selectedTagToAdd"
        class="form-select mb-2"
        @change="addTag"
      >
        <option value="">+ Select tag...</option>
        <option
          v-for="tag in availableTagOptions"
          :key="`tag-option-${tag.value}`"
          :value="tag.value"
        >
          {{ tag.text }}
        </option>
      </select>

      <!-- Selected Tags Pills -->
      <div v-if="selectedTags.length > 0" class="selected-pills">
        <span
          v-for="tag in selectedTags"
          :key="`selected-tag-${tag}`"
          class="pill tag-pill"
        >
          {{ tag }}
          <font-awesome-icon
            icon="times"
            class="pill-remove"
            @click="removeTag(tag)"
            title="Remove"
          />
        </span>
      </div>
    </div>

    <div class="d-grid gap-2">
      <BButton
        variant="success"
        size="lg"
        :disabled="!canSave || isLoading || selectedDiagnoses.length === 0"
        @click="handleSaveAndNext"
      >
        <BSpinner v-if="isLoading" small class="me-2" />
        <font-awesome-icon v-else icon="save" class="me-2" />
        Save & Next
      </BButton>

      <BButton
        variant="info"
        :disabled="isLoading || hasAnnotations || selectedDiagnoses.length > 0"
        @click="handleSaveAsNormal"
      >
        <font-awesome-icon icon="check-circle" class="me-2" />
        Mark as Normal
      </BButton>

      <BButton
        v-if="aiEnabled"
        variant="primary"
        :disabled="aiLoading || isLoading"
        @click="handleAISegment"
      >
        <BSpinner v-if="aiLoading" small class="me-2" />
        <font-awesome-icon v-else icon="robot" class="me-2" />
        AI Segment
      </BButton>

      <!-- Reject Button Dropdown -->
      <BDropdown
        variant="danger"
        :disabled="isLoading || hasAnnotations"
        block
        class="reject-dropdown"
      >
        <template #button-content>
          <font-awesome-icon icon="ban" class="me-2" />
          Reject Image
        </template>
        <BDropdownItem
          v-for="option in rejectOptions"
          :key="option.value"
          @click="selectRejectReason(option.value)"
        >
          {{ option.label }}
        </BDropdownItem>
      </BDropdown>
    </div>

    <!-- Reject Confirmation Modal -->
    <BModal
      v-model="isRejectModalVisible"
      title="Reject Image"
      ok-title="Confirm Reject"
      title-class="text-white h4"
      cancel-title="Cancel"
      ok-variant="danger"
      cancel-variant="secondary"
      @ok="handleRejectConfirm"
      @cancel="handleRejectCancel"
    >
      <p v-if="selectedRejectReason !== 'Other'" class="mb-3">
        Are you sure you want to reject this image with the following reason?
      </p>
      <p v-if="selectedRejectReason !== 'Other'" class="fw-bold reject-reason-text mb-0">
        {{ selectedRejectReason }}
      </p>

      <div v-if="selectedRejectReason === 'Other'">
        <p class="mb-3">Please enter the reason for rejecting this image:</p>
        <input
          v-model="customRejectReason"
          type="text"
          class="form-control modal-input"
          placeholder="Enter custom reason..."
          @keyup.enter="handleRejectConfirm"
        />
      </div>
    </BModal>

    <div class="mt-3 text-center">
      <small class="text-muted">
        Images remaining: {{ unlabeledCount }}
      </small>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import { rejectService } from '@/services/reject.service'

const emit = defineEmits(['save-and-next', 'save-as-normal', 'ai-segment', 'reject'])

const store = useStore()

// Temporary selection holders for dropdowns
const selectedDiagnosisToAdd = ref('')
const selectedTagToAdd = ref('')
const customDiagnosisText = ref('')
const selectedRejectReason = ref('')
const customRejectReason = ref('')
const isRejectModalVisible = ref(false)

// Reject options
const rejectOptions = [
  { value: 'Poor Quality', label: 'Poor Quality' },
  { value: 'Not referenced image', label: 'Not referenced image' },
  { value: 'Require Second Opinion', label: 'Require Second Opinion' },
  { value: 'Other', label: 'Other' }
]

const selectedDiagnoses = computed({
  get: () => store.state.labelerAnnotation.selectedDiagnoses,
  set: (value) => store.commit('labelerAnnotation/SET_SELECTED_DIAGNOSES', value)
})

const selectedTags = computed({
  get: () => store.state.labelerAnnotation.selectedTags,
  set: (value) => store.commit('labelerAnnotation/SET_SELECTED_TAGS', value)
})

const comments = computed({
  get: () => store.state.labelerAnnotation.comments,
  set: (value) => store.commit('labelerAnnotation/SET_COMMENTS', value)
})

// All diagnosis options
const diagnosisOptions = computed(() => {
  const options = store.state.labelerAnnotation.diagnosisOptions.map(d => ({
    text: d.label || d,
    value: d.value || d.label || d
  }))
  return options
})

// Filter out already selected diagnoses
const availableDiagnosisOptions = computed(() => {
  return diagnosisOptions.value.filter(d => !selectedDiagnoses.value.includes(d.value))
})

// All tag options
const tagOptions = computed(() =>
  store.state.labelerAnnotation.tagOptions.map(tag => ({
    text: tag,
    value: tag
  }))
)

// Filter out already selected tags
const availableTagOptions = computed(() => {
  return tagOptions.value.filter(t => !selectedTags.value.includes(t.value))
})

const canSave = computed(() => store.getters['labelerAnnotation/canSave'])
const isLoading = computed(() => store.state.labelerAnnotation.isLoading)
const aiLoading = computed(() => store.state.labelerAnnotation.aiLoading)
const aiEnabled = computed(() => store.state.labelerAnnotation.aiEnabled)
const unlabeledCount = computed(() => store.state.labelerImageQueue.unlabeledCount)
const hasAnnotations = computed(() => store.state.labelerAnnotation.annotations.length > 0)

// Check if "Other" is selected for diagnosis (case-insensitive)
const isOtherDiagnosisSelected = computed(() => {
  return selectedDiagnosisToAdd.value.toLowerCase() === 'other'
})

// Get diagnosis label from value
const getDiagnosisLabel = (value) => {
  const diagnosis = diagnosisOptions.value.find(d => d.value === value)
  return diagnosis ? diagnosis.text : value
}

// Add diagnosis from dropdown
const addDiagnosis = () => {
  if (selectedDiagnosisToAdd.value) {
    // If "Other" is selected, wait for custom text input
    if (isOtherDiagnosisSelected.value) {
      customDiagnosisText.value = ''
      return
    }

    const current = [...selectedDiagnoses.value]
    if (!current.includes(selectedDiagnosisToAdd.value)) {
      current.push(selectedDiagnosisToAdd.value)
      selectedDiagnoses.value = current
    }
    // Reset dropdown
    selectedDiagnosisToAdd.value = ''
  }
}

// Add custom diagnosis when Enter is pressed
const addCustomDiagnosis = () => {
  if (customDiagnosisText.value.trim()) {
    const current = [...selectedDiagnoses.value]
    const customValue = customDiagnosisText.value.trim()

    if (!current.includes(customValue)) {
      current.push(customValue)
      selectedDiagnoses.value = current
    }

    // Reset
    customDiagnosisText.value = ''
    selectedDiagnosisToAdd.value = ''
  }
}

// Remove diagnosis pill
const removeDiagnosis = (diagnosisValue) => {
  const current = [...selectedDiagnoses.value]
  const index = current.indexOf(diagnosisValue)
  if (index > -1) {
    current.splice(index, 1)
    selectedDiagnoses.value = current
  }
}

// Add tag from dropdown
const addTag = () => {
  if (selectedTagToAdd.value) {
    const current = [...selectedTags.value]
    if (!current.includes(selectedTagToAdd.value)) {
      current.push(selectedTagToAdd.value)
      selectedTags.value = current
    }
    // Reset dropdown
    selectedTagToAdd.value = ''
  }
}

// Remove tag pill
const removeTag = (tagValue) => {
  const current = [...selectedTags.value]
  const index = current.indexOf(tagValue)
  if (index > -1) {
    current.splice(index, 1)
    selectedTags.value = current
  }
}

const handleSaveAndNext = () => {
  emit('save-and-next')
}

const handleSaveAsNormal = () => {
  emit('save-as-normal')
}

const handleAISegment = () => {
  emit('ai-segment')
}

const selectRejectReason = (reason) => {
  selectedRejectReason.value = reason

  // Clear custom reason if reopening
  customRejectReason.value = ''

  // Show modal
  isRejectModalVisible.value = true
}

const handleRejectCancel = () => {
  // Reset selections
  selectedRejectReason.value = ''
  customRejectReason.value = ''
  isRejectModalVisible.value = false
}

const handleRejectConfirm = async () => {
  // Determine the reason to send
  let reasonToSend = selectedRejectReason.value

  // If "Other" is selected, use custom reason
  if (selectedRejectReason.value === 'Other') {
    if (!customRejectReason.value.trim()) {
      // Don't proceed if custom reason is empty
      return
    }
    reasonToSend = customRejectReason.value.trim()
  }

  const currentImage = store.state.labelerImageQueue.currentImage
  const projectId = store.state.labelerImageQueue.projectId

  if (!currentImage || !projectId) {
    console.error('No current image or project ID')
    handleRejectCancel()
    return
  }

  try {
    await rejectService.reject(projectId, [currentImage._id], reasonToSend, 3)

    // Clear annotations and state
    store.dispatch('labelerAnnotation/clearAnnotations')

    // Emit reject event so parent can fetch next image
    emit('reject')

    // Reset and close modal
    handleRejectCancel()
  } catch (error) {
    console.error('Failed to reject image:', error)
    handleRejectCancel()
  }
}
</script>

<style scoped>
.workflow-actions {
  padding: 1rem;
  background-color: var(--color-bg-secondary);
  border: var(--border-width-card) solid var(--color-border-secondary);
  border-radius: var(--border-radius-standard);
}

.workflow-actions h6 {
  color: #ffffff;
  font-weight: 900;
  letter-spacing: 0.05em;
}

.workflow-actions label {
  color: #ffffff;
  font-weight: 900;
}

.workflow-actions :deep(.form-check-label) {
  color: #ffffff;
}

.workflow-actions :deep(.form-check-input) {
  background-color: #15385b;
  border-color: #3981c8;
}

.workflow-actions :deep(.form-check-input:checked) {
  background-color: #f0b93b;
  border-color: #f0b93b;
}

.workflow-actions :deep(.form-control) {
  background-color: #15385b;
  color: #ffffff;
  border-color: #3981c8;
}

.workflow-actions :deep(.form-control:focus) {
  background-color: #15385b;
  color: #ffffff;
  border-color: #f0b93b;
  box-shadow: 0 0 0 0.2rem rgba(240, 185, 59, 0.25);
}

.workflow-actions :deep(.btn-success) {
  background-color: #28a745;
  border-color: #28a745;
  font-weight: 900;
}

.workflow-actions :deep(.btn-success:hover) {
  background-color: #218838;
  border-color: #1e7e34;
}

.workflow-actions :deep(.btn-info) {
  background-color: #3981c8;
  border-color: #3981c8;
  color: #ffffff;
  font-weight: 900;
}

.workflow-actions :deep(.btn-info:hover) {
  background-color: #2e6ba3;
  border-color: #2e6ba3;
}

.workflow-actions :deep(.btn-primary) {
  background-color: #f0b93b;
  border-color: #f0b93b;
  color: #15385b;
  font-weight: 900;
}

.workflow-actions :deep(.btn-primary:hover) {
  background-color: #d9a532;
  border-color: #d9a532;
}

.text-muted {
  color: #878787 !important;
}

/* Pills Container */
.selected-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  min-height: 32px;
}

/* Pill Base Styles */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.75rem;
  border-radius: 1.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #ffffff;
  transition: all 0.2s ease;
  cursor: default;
  white-space: nowrap;
}

/* Diagnosis Pills - Green theme */
.diagnosis-pill {
  background-color: #28a745;
  border: 1px solid #28a745;
}

.diagnosis-pill:hover {
  background-color: #218838;
  box-shadow: 0 2px 8px rgba(40, 167, 69, 0.3);
}

/* Tag Pills - Blue theme */
.tag-pill {
  background-color: #3981c8;
  border: 1px solid #3981c8;
}

.tag-pill:hover {
  background-color: #2e6ba3;
  box-shadow: 0 2px 8px rgba(57, 129, 200, 0.3);
}

/* Remove Icon */
.pill-remove {
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0.15rem;
  border-radius: 50%;
  transition: all 0.15s ease;
  opacity: 0.8;
}

.pill-remove:hover {
  opacity: 1;
  background-color: rgba(255, 255, 255, 0.2);
  transform: scale(1.2);
}

/* Dropdown Styling */
.workflow-actions .form-select {
  background-color: #15385b;
  color: #ffffff;
  border: 1px solid #3981c8;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.workflow-actions .form-select:hover {
  border-color: #f0b93b;
}

.workflow-actions .form-select:focus {
  background-color: #15385b;
  color: #ffffff;
  border-color: #f0b93b;
  box-shadow: 0 0 0 0.2rem rgba(240, 185, 59, 0.25);
}

.workflow-actions .form-select option {
  background-color: #15385b;
  color: #ffffff;
}

.workflow-actions .form-select option:hover {
  background-color: var(--color-bg-primary);
}

/* Reject Button Dropdown Styling */
.reject-dropdown {
  width: 100%;
}

.reject-dropdown :deep(.btn-group) {
  width: 100%;
  display: block;
}

.reject-dropdown :deep(.btn-danger) {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #ffffff;
  font-weight: 900;
  transition: all 0.2s ease;
  width: 100%;
}

.reject-dropdown :deep(.btn-danger:hover:not(:disabled)) {
  background-color: #c82333;
  border-color: #bd2130;
  box-shadow: 0 2px 8px rgba(220, 53, 69, 0.4);
}

.reject-dropdown :deep(.btn-danger:focus) {
  background-color: #dc3545;
  border-color: #bd2130;
  box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.5);
}

.reject-dropdown :deep(.btn-danger:disabled) {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #dc3545;
  border-color: #dc3545;
}

.reject-dropdown :deep(.dropdown-menu) {
  background-color: #15385b;
  border: 2px solid #dc3545;
  width: 100%;
}

.reject-dropdown :deep(.dropdown-item) {
  color: #ffffff;
  font-weight: 600;
  padding: 0.5rem 1rem;
  transition: all 0.2s ease;
}

.reject-dropdown :deep(.dropdown-item:hover) {
  background-color: #dc3545;
  color: #ffffff;
}

.reject-dropdown :deep(.dropdown-item:active) {
  background-color: #c82333;
  color: #ffffff;
}

/* Modal Styling Overrides */
:deep(.modal-content) {
  background-color: #15385b;
  color: #ffffff;
  border: 2px solid #f0b93b;
}

:deep(.modal-header) {
  background-color: var(--color-bg-primary);
  border-bottom: 2px solid #dc3545;
}

:deep(.modal-title) {
  color: #ffffff !important;
  font-weight: 900;
  letter-spacing: 0.05em;
}

:deep(.modal-header .btn-close) {
  filter: brightness(0) invert(1);
  opacity: 0.8;
}

:deep(.modal-header .btn-close:hover) {
  opacity: 1;
}

:deep(.modal-body) {
  color: #ffffff;
}

.reject-reason-text {
  color: #dc3545 !important;
  font-size: 1.1rem;
}

.modal-input {
  background-color: var(--color-bg-primary);
  color: #ffffff;
  border: 1px solid #3981c8;
}

.modal-input:focus {
  background-color: var(--color-bg-primary);
  color: #ffffff;
  border-color: #f0b93b;
  box-shadow: 0 0 0 0.2rem rgba(240, 185, 59, 0.25);
}

/* Modal Footer Buttons */
:deep(.modal-footer) {
  background-color: #15385b;
  border-top: 1px solid #3981c8;
}

:deep(.modal-footer .btn-danger) {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #ffffff;
  font-weight: 900;
}

:deep(.modal-footer .btn-danger:hover) {
  background-color: #c82333;
  border-color: #bd2130;
}

:deep(.modal-footer .btn-secondary) {
  background-color: #3981c8;
  border-color: #3981c8;
  color: #ffffff;
  font-weight: 900;
}

:deep(.modal-footer .btn-secondary:hover) {
  background-color: #2e6ba3;
  border-color: #2e6ba3;
}
</style>
